import { CSSProperties, ReactNode, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import { LinkBox } from '@sorare/core/src/atoms/navigation/Box';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useTouchScreen } from '@sorare/core/src/hooks/device/useTouchScreen';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import proTitle from 'assets/home/pro-title.svg';

import background from './assets/background.jpg';
import haalandAvif from './assets/haaland.avif';
import haaland from './assets/haaland.png';
import macallisterAvif from './assets/macallister.avif';
import macallister from './assets/macallister.png';
import palmerAvif from './assets/palmer.avif';
import palmer from './assets/palmer.png';
import riceAvif from './assets/rice.avif';
import rice from './assets/rice.png';

const RootLinkBox = styled(LinkBox)`
  position: relative;
  isolation: isolate;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: var(--double-unit);
  overflow: hidden;

  @media ${laptopAndAbove} {
    padding: var(--double-unit);
    padding-bottom: calc(6 * var(--unit));
  }
`;
const BackgroundImg = styled(animated.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -8;
  object-fit: cover;
`;
const Picture = styled.picture`
  position: absolute;
  inset: 0;
`;
/* transform: translateX values on players are set to have their heads centered rather than the whole image centered */
const PlayerImg = styled(animated.img)`
  position: absolute;
  height: calc(100% - var(--padding-top));
  bottom: 0;
  object-fit: contain;
  transform-origin: bottom center;
`;
const Player1Img = styled(PlayerImg)`
  transform: translateX(-53%) scale(0.87);
  z-index: -6;
`;
const Player2Img = styled(PlayerImg)`
  transform: translateX(-58%) scale(0.95);
  z-index: -4;
`;
const Player3Img = styled(PlayerImg)`
  transform: translateX(-36%) scale(0.95);
  z-index: -3;
`;
const Player4Img = styled(PlayerImg)`
  transform: translateX(-48%) scale(0.87);
  z-index: -5;
`;
const DarkGradients = styled.div`
  position: absolute;
  inset: 0;
`;
const DarkGradientsBelowPlayers = styled(DarkGradients)`
  z-index: -7;
  background-image: linear-gradient(
      to right,
      transparent 70%,
      var(--c-black) 100%
    ),
    linear-gradient(to right, var(--c-black) 0%, transparent 30%),
    linear-gradient(to bottom, var(--c-black) 0%, transparent 30%);
`;
const DarkGradientsAbovePlayers = styled(DarkGradients)`
  z-index: -2;
  background-image: linear-gradient(to bottom, transparent 50%, var(--c-black));
`;
const ProTitleImg = styled(animated.img)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  height: calc(6 * var(--unit));
  z-index: -1;

  @media ${laptopAndAbove} {
    height: calc(8 * var(--unit));
  }
`;

const config = {
  mass: 1,
  tension: 300,
  friction: 20,
};

const springConfigs = {
  player1: {
    initialPosition: {
      from: { left: '20%' },
      config,
    },
    position1: {
      to: { left: '18%' },
    },
    position2: {
      to: { left: '20%' },
    },
  },
  player2: {
    initialPosition: {
      from: { left: '40%' },
      config,
    },
    position1: {
      to: { left: '39%' },
    },
    position2: {
      to: { left: '40%' },
    },
  },
  player3: {
    initialPosition: {
      from: { left: '60%' },
      config,
    },
    position1: {
      to: { left: '61%' },
    },
    position2: {
      to: { left: '60%' },
    },
  },
  player4: {
    initialPosition: {
      from: { left: '80%' },
      config,
    },
    position1: {
      to: { left: '82%' },
    },
    position2: {
      to: { left: '80%' },
    },
  },
  proLogo: {
    initialPosition: {
      from: { bottom: 112 },
      config,
    },
    position1: {
      to: { bottom: 120 },
    },
    position2: {
      to: { bottom: 112 },
    },
  },
  proLogoLaptop: {
    initialPosition: {
      from: { bottom: 140 },
      config,
    },
    position1: {
      to: { bottom: 148 },
    },
    position2: {
      to: { bottom: 140 },
    },
  },
  background: {
    initialPosition: {
      from: { transform: 'scale(1)' },
      config,
    },
    position1: {
      to: { transform: 'scale(1.1)' },
    },
    position2: {
      to: { transform: 'scale(1)' },
    },
  },
};

type Props = { children: ReactNode; withBanner: boolean };
export const ProBlockLinkBox = ({ children, withBanner }: Props) => {
  const isTouchScreen = useTouchScreen();
  const { formatMessage } = useIntl();
  const isLaptop = useIsLaptop();

  const [style1, api1] = useSpring(() => springConfigs.player1.initialPosition);
  const [style2, api2] = useSpring(() => springConfigs.player2.initialPosition);
  const [style3, api3] = useSpring(() => springConfigs.player3.initialPosition);
  const [style4, api4] = useSpring(() => springConfigs.player4.initialPosition);
  const [style5, api5] = useSpring(() =>
    isLaptop
      ? springConfigs.proLogoLaptop.initialPosition
      : springConfigs.proLogo.initialPosition
  );
  const [style6, api6] = useSpring(
    () => springConfigs.background.initialPosition
  );

  useEffect(() => {
    if (isTouchScreen) {
      api1.start(springConfigs.player1.position1);
      api2.start(springConfigs.player2.position1);
      api3.start(springConfigs.player3.position1);
      api4.start(springConfigs.player4.position1);
      api5.start(
        isLaptop
          ? springConfigs.proLogoLaptop.position1
          : springConfigs.proLogo.position1
      );
      api6.start(springConfigs.background.position1);
    }
  }, [api1, api2, api3, api4, api5, api6, isLaptop, isTouchScreen]);
  return (
    <RootLinkBox
      onMouseEnter={() => {
        api1.start(springConfigs.player1.position1);
        api2.start(springConfigs.player2.position1);
        api3.start(springConfigs.player3.position1);
        api4.start(springConfigs.player4.position1);
        api5.start(
          isLaptop
            ? springConfigs.proLogoLaptop.position1
            : springConfigs.proLogo.position1
        );
        api6.start(springConfigs.background.position1);
      }}
      onMouseLeave={() => {
        api1.start(springConfigs.player1.position2);
        api2.start(springConfigs.player2.position2);
        api3.start(springConfigs.player3.position2);
        api4.start(springConfigs.player4.position2);
        api5.start(
          isLaptop
            ? springConfigs.proLogoLaptop.position2
            : springConfigs.proLogo.position2
        );
        api6.start(springConfigs.background.position2);
      }}
      style={
        {
          '--padding-top': withBanner
            ? 'calc(14 * var(--unit))'
            : 'var(--double-unit)',
        } as CSSProperties
      }
    >
      <BackgroundImg src={background} alt="" style={style6} />
      <Picture>
        <source srcSet={palmerAvif} type="image/avif" />
        <Player1Img src={palmer} alt="" style={style1} />
      </Picture>
      <Picture>
        <source srcSet={haalandAvif} type="image/avif" />
        <Player2Img src={haaland} alt="" style={style2} />
      </Picture>
      <Picture>
        <source srcSet={riceAvif} type="image/avif" />
        <Player3Img src={rice} alt="" style={style3} />
      </Picture>
      <Picture>
        <source srcSet={macallisterAvif} type="image/avif" />
        <Player4Img src={macallister} alt="" style={style4} />
      </Picture>
      <DarkGradientsBelowPlayers />
      <DarkGradientsAbovePlayers />
      <ProTitleImg
        src={proTitle}
        height={62}
        alt={formatMessage(glossary.pro)}
        style={style5}
      />
      {children}
    </RootLinkBox>
  );
};
